import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import SyncManager from '@/components/SyncManager';
import Login from '@/pages/Login';
import AppSelect from '@/pages/AppSelect';
import Dashboard from '@/pages/Dashboard';
import Subscribers from '@/pages/Subscribers';
import DueDates from '@/pages/DueDates';
import Payments from '@/pages/Payments';
import Expenses from '@/pages/Expenses';
import Invoices from '@/pages/Invoices';
import Support from '@/pages/Support';
import Announcements from '@/pages/Announcements';
import Users from '@/pages/Users';
import CustomerSupport from '@/pages/CustomerSupport';
import SubscriberApp from '@/pages/SubscriberApp';
import TimesheetManagement from '@/pages/TimesheetManagement';
import Payslips from '@/pages/Payslips';
import Vendo from '@/pages/Vendo';
import PayrollSummary from '@/pages/PayrollSummary';
import ResetTimesheets from '@/pages/ResetTimesheets';
import CashAdvance from '@/pages/CashAdvance';
import PWAGuide from '@/pages/PWAGuide';
import BackupRestore from '@/pages/BackupRestore';
import Reminders from '@/pages/Reminders';
import PayslipArchive from '@/pages/PayslipArchive';
import TimeClock from '@/pages/TimeClock';
import TimeClockAdmin from '@/pages/TimeClockAdmin';
import OfficeLocations from '@/pages/OfficeLocations';
import OvertimeManagement from '@/pages/OvertimeManagement';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';

// NAS (Nenen's Agrivet Supply) imports
import {
  NASLayout,
  NASDashboard,
  NASProducts,
  NASOrders,
  NASBodega,
  NASShop,
  NASDeliveries,
  NASReports
} from '@/pages/nas';
import NASCustomers from '@/pages/nas/NASCustomers';
import NASCustomerOrders from '@/pages/nas/NASCustomerOrders';
import NASOrderApproval from '@/pages/nas/NASOrderApproval';
import NASPayments from '@/pages/nas/NASPayments';
import NASPOS from '@/pages/nas/NASPOS';
import NASPOSAdmin from '@/pages/nas/NASPOSAdmin';
import BodegaSupplierOrders from '@/pages/nas/BodegaSupplierOrders';
import NASShopOrders from '@/pages/nas/NASShopOrders';
import NASSunjinMonitor from '@/pages/nas/NASSunjinMonitor';
import NASInvoices from '@/pages/nas/NASInvoices';
import NASBankDeposits from '@/pages/nas/NASBankDeposits';
import ShoppingHub from '@/pages/nas/ShoppingHub';
import SunjinShopOrders from '@/pages/nas/SunjinShopOrders';
import BodegaToNASOrders from '@/pages/nas/BodegaToNASOrders';
import AdminHub from '@/pages/nas/AdminHub';

// Customer Portal imports
import {
  CustomerLogin,
  CustomerLayout,
  CustomerCatalog,
  CustomerCart,
  CustomerOrders,
  CustomerProfile,
  CustomerInvoices
} from '@/pages/nas/CustomerPortal';
import CustomerNotifications from '@/pages/nas/CustomerPortal/CustomerNotifications';

// Redirect based on selected app or user access level
const HomeRedirect = () => {
  const { user } = useAuth();
  
  // Check if user has selected an app
  const selectedApp = localStorage.getItem('selectedApp');
  
  // Check if user has NAS access
  const hasNASAccess = user?.role === 'admin' || 
    user?.nas_access === true || 
    user?.nas_access === "true" ||
    user?.nas_access === 1;
  
  // Debug log
  console.log('HomeRedirect:', {
    username: user?.username,
    role: user?.role,
    nas_access: user?.nas_access,
    hasNASAccess,
    selectedApp,
    redirectTo: hasNASAccess ? '/select-app' : (selectedApp === 'arbe' ? '/arbe/dashboard' : '/select-app')
  });
  
  // If user has NAS access, they should see app selection (not auto-redirect to ARBE)
  // This ensures users with NAS access can choose which app to use
  if (hasNASAccess) {
    return <Navigate to="/select-app" replace />;
  }
  
  // If app is selected and user doesn't have NAS access, redirect to that app
  if (selectedApp === 'nas') {
    return <Navigate to="/nas/dashboard" replace />;
  }
  if (selectedApp === 'arbe') {
    return <Navigate to="/arbe/dashboard" replace />;
  }
  
  // Otherwise go to app selection
  return <Navigate to="/select-app" replace />;
};

// ARBE Home Redirect (within ARBE app)
const ARBEHomeRedirect = () => {
  const { user } = useAuth();
  
  // Users who can see the full dashboard (admin + specific staff)
  const dashboardAccessUsers = ['alma', 'jelyn', 'nicole', 'carla', 'kristine', 'pabs'];
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase().replace(/\s+/g, '') || '';
  
  // Check if current user has dashboard access
  const hasDashboardAccess = user?.role === 'admin' || 
    dashboardAccessUsers.some(name => username.includes(name) || fullName.includes(name));
  
  if (hasDashboardAccess) {
    return <Navigate to="/arbe/dashboard" replace />;
  } else {
    // Staff go directly to Time Clock for clock in/out
    return <Navigate to="/arbe/time-clock" replace />;
  }
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// NAS Protected Route - all authenticated users can access
const NASProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Customer Support Portal - No login required */}
      <Route path="/customer-support" element={<CustomerSupport />} />
      
      {/* Subscriber PWA App - No admin login required */}
      <Route path="/app" element={<SubscriberApp />} />
      <Route path="/app/*" element={<SubscriberApp />} />
      
      {/* Login */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* App Selection Page */}
      <Route path="/select-app" element={
        <ProtectedRoute>
          <AppSelect />
        </ProtectedRoute>
      } />
      
      {/* Root redirect */}
      <Route path="/" element={
        <ProtectedRoute>
          <HomeRedirect />
        </ProtectedRoute>
      } />
      
      {/* ============ ARBE Routes (Existing Mipay) ============ */}
      <Route path="/arbe" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<ARBEHomeRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="subscribers" element={<Subscribers />} />
        <Route path="due-dates" element={<DueDates />} />
        <Route path="payments" element={<Payments />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="timesheet" element={<TimesheetManagement />} />
        <Route path="time-clock" element={<TimeClock />} />
        <Route path="time-clock-admin" element={<TimeClockAdmin />} />
        <Route path="office-locations" element={<OfficeLocations />} />
        <Route path="overtime" element={<OvertimeManagement />} />
        <Route path="payroll-summary" element={<PayrollSummary />} />
        <Route path="reset-timesheets" element={<ResetTimesheets />} />
        <Route path="cash-advance" element={<CashAdvance />} />
        <Route path="payslips" element={<Payslips />} />
        <Route path="payslip-archive" element={<PayslipArchive />} />
        <Route path="vendo" element={<Vendo />} />
        <Route path="support" element={<Support />} />
        <Route path="announcements" element={<Announcements />} />
        <Route path="users" element={<Users />} />
        <Route path="mobile-app" element={<PWAGuide />} />
        <Route path="backup" element={<BackupRestore />} />
        <Route path="reminders" element={<Reminders />} />
      </Route>
      
      {/* Legacy routes - redirect to /arbe/* */}
      <Route path="/dashboard" element={<Navigate to="/arbe/dashboard" replace />} />
      <Route path="/subscribers" element={<Navigate to="/arbe/subscribers" replace />} />
      <Route path="/due-dates" element={<Navigate to="/arbe/due-dates" replace />} />
      <Route path="/payments" element={<Navigate to="/arbe/payments" replace />} />
      <Route path="/expenses" element={<Navigate to="/arbe/expenses" replace />} />
      <Route path="/invoices" element={<Navigate to="/arbe/invoices" replace />} />
      <Route path="/timesheet" element={<Navigate to="/arbe/timesheet" replace />} />
      <Route path="/payroll-summary" element={<Navigate to="/arbe/payroll-summary" replace />} />
      <Route path="/reset-timesheets" element={<Navigate to="/arbe/reset-timesheets" replace />} />
      <Route path="/cash-advance" element={<Navigate to="/arbe/cash-advance" replace />} />
      <Route path="/payslips" element={<Navigate to="/arbe/payslips" replace />} />
      <Route path="/payslip-archive" element={<Navigate to="/arbe/payslip-archive" replace />} />
      <Route path="/vendo" element={<Navigate to="/arbe/vendo" replace />} />
      <Route path="/support" element={<Navigate to="/arbe/support" replace />} />
      <Route path="/announcements" element={<Navigate to="/arbe/announcements" replace />} />
      <Route path="/users" element={<Navigate to="/arbe/users" replace />} />
      <Route path="/mobile-app" element={<Navigate to="/arbe/mobile-app" replace />} />
      <Route path="/backup" element={<Navigate to="/arbe/backup" replace />} />
      <Route path="/reminders" element={<Navigate to="/arbe/reminders" replace />} />
      
      {/* ============ NAS Routes (Nenen's Agrivet Supply) ============ */}
      <Route path="/nas" element={
        <NASProtectedRoute>
          <NASLayout />
        </NASProtectedRoute>
      }>
        <Route index element={<Navigate to="/nas/dashboard" replace />} />
        <Route path="dashboard" element={<NASDashboard />} />
        <Route path="products" element={<NASProducts />} />
        <Route path="sunjin-monitor" element={<NASSunjinMonitor />} />
        <Route path="shopping" element={<ShoppingHub />} />
        <Route path="supplier-orders" element={<ShoppingHub />} />
        <Route path="sunjin-orders" element={<SunjinShopOrders />} />
        <Route path="bodega-to-nas" element={<BodegaToNASOrders />} />
        <Route path="bodega-orders" element={<BodegaSupplierOrders />} />
        <Route path="shop-orders" element={<NASShopOrders />} />
        <Route path="orders" element={<NASOrders />} />
        <Route path="bodega" element={<NASBodega />} />
        <Route path="customers" element={<NASCustomers />} />
        <Route path="customer-orders" element={<NASCustomerOrders />} />
        <Route path="order-approval" element={<NASOrderApproval />} />
        <Route path="shop" element={<NASShop />} />
        <Route path="pos" element={<NASPOS />} />
        <Route path="pos-admin" element={<NASPOSAdmin />} />
        <Route path="admin" element={<AdminHub />} />
        <Route path="payments" element={<NASPayments />} />
        <Route path="invoices" element={<NASInvoices />} />
        <Route path="bank-deposits" element={<NASBankDeposits />} />
        <Route path="deliveries" element={<NASDeliveries />} />
        <Route path="reports" element={<NASReports />} />
      </Route>
      
      {/* ============ NAS Customer Portal (Public - No Admin Login) ============ */}
      <Route path="/nas-portal/login" element={<CustomerLogin />} />
      <Route path="/nas-portal" element={<CustomerLayout />}>
        <Route index element={<Navigate to="/nas-portal/catalog" replace />} />
        <Route path="catalog" element={<CustomerCatalog />} />
        <Route path="cart" element={<CustomerCart />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="invoices" element={<CustomerInvoices />} />
        <Route path="notifications" element={<CustomerNotifications />} />
        <Route path="profile" element={<CustomerProfile />} />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <div className="App">
            <AppRoutes />
            <SyncManager />
            <Toaster position="top-right" />
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
